import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Box,
  Alert,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { adminService } from '../services/api';

const AdminForm = ({ open, onClose, admin, onSuccess, currentAdminId }) => {
  const { t } = useTranslation();
  const isEdit = !!admin;

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'admin',
    isActive: true
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null);

  useEffect(() => {
    if (admin) {
      setFormData({
        username: admin.username || '',
        email: admin.email || '',
        password: '',
        confirmPassword: '',
        role: admin.role || 'admin',
        isActive: admin.isActive !== false
      });
    } else {
      setFormData({
        username: '',
        email: '',
        password: '',
        confirmPassword: '',
        role: 'admin',
        isActive: true
      });
    }
    setErrors({});
    setAlert(null);
  }, [admin, open]);

  const isCurrentAdmin = admin && (admin._id === currentAdminId || admin.id === currentAdminId);

  const validateForm = () => {
    const newErrors = {};

    if (!isEdit && !formData.username) {
      newErrors.username = 'Username is required';
    }

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!isEdit) {
      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (formData.password.length < 6) {
        newErrors.password = t('admins.passwordRequirements');
      }

      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = t('admins.passwordMismatch');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field) => (event) => {
    const value = field === 'isActive' ? event.target.checked : event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setAlert(null);

    try {
      let response;
      if (isEdit) {
        const updateData = {
          email: formData.email,
          role: formData.role,
          isActive: formData.isActive
        };
        response = await adminService.update(admin._id || admin.id, updateData);
        onSuccess(t('admins.adminUpdated'));
      } else {
        const createData = {
          username: formData.username,
          email: formData.email,
          password: formData.password,
          role: formData.role
        };
        response = await adminService.create(createData);
        onSuccess(t('admins.adminCreated'));
      }
      onClose();
    } catch (error) {
      console.error('Error saving admin:', error);
      const errorMessage = error.response?.data?.error || 'Failed to save administrator';
      setAlert({ type: 'error', message: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEdit ? t('admins.edit') : t('admins.add')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {alert && (
            <Alert severity={alert.type}>
              {alert.message}
            </Alert>
          )}

          {!isEdit && (
            <TextField
              label={t('admins.username')}
              value={formData.username}
              onChange={handleChange('username')}
              error={!!errors.username}
              helperText={errors.username}
              fullWidth
              required
              disabled={loading}
            />
          )}

          {isEdit && (
            <TextField
              label={t('admins.username')}
              value={formData.username}
              fullWidth
              disabled
            />
          )}

          <TextField
            label={t('admins.email')}
            type="email"
            value={formData.email}
            onChange={handleChange('email')}
            error={!!errors.email}
            helperText={errors.email}
            fullWidth
            required
            disabled={loading}
          />

          {!isEdit && (
            <>
              <TextField
                label={t('admins.newPassword')}
                type="password"
                value={formData.password}
                onChange={handleChange('password')}
                error={!!errors.password}
                helperText={errors.password || t('admins.passwordRequirements')}
                fullWidth
                required
                disabled={loading}
              />

              <TextField
                label={t('admins.confirmNewPassword')}
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange('confirmPassword')}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword}
                fullWidth
                required
                disabled={loading}
              />
            </>
          )}

          <FormControl fullWidth>
            <InputLabel>{t('admins.role')}</InputLabel>
            <Select
              value={formData.role}
              onChange={handleChange('role')}
              label={t('admins.role')}
              disabled={loading || isCurrentAdmin}
            >
              <MenuItem value="superadmin">{t('admins.roles.superadmin')}</MenuItem>
              <MenuItem value="admin">{t('admins.roles.admin')}</MenuItem>
              <MenuItem value="editor">{t('admins.roles.editor')}</MenuItem>
            </Select>
            {isCurrentAdmin && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                {t('admins.cannotChangeOwnRole')}
              </Typography>
            )}
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={handleChange('isActive')}
                disabled={loading || isCurrentAdmin}
              />
            }
            label={t('admins.active')}
          />
          {isCurrentAdmin && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
              {t('admins.cannotDeactivateSelf')}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
        >
          {loading ? t('common.saving') : t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdminForm;